import {
  ChevronLeft as ChevronLeftIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import {
  alpha,
  Box,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useCallback, useState } from "react";

interface SidebarShellProps {
  title: string;
  icon: React.ReactNode;
  totalCount: number;
  displayedCount: number;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  searchPlaceholder?: string;
  isLoading?: boolean;
  isLoadingMore?: boolean;
  width?: number;
  leftOffset?: number;
  zIndex?: number;
  toggleButtonTop?: string;
  toggleButtonColor?: "primary" | "warning";
  onSidebarToggle?: (isOpen: boolean) => void;
  headerExtra?: React.ReactNode;
  defaultOpen?: boolean;
  children: React.ReactNode;
}

export const SidebarShell: React.FC<SidebarShellProps> = ({
  title,
  icon,
  totalCount,
  displayedCount,
  searchQuery,
  onSearchChange,
  searchPlaceholder = "Search...",
  isLoading = false,
  isLoadingMore = false,
  width = 280,
  leftOffset = 0,
  zIndex = 1320,
  toggleButtonTop = "8%",
  toggleButtonColor = "primary",
  onSidebarToggle,
  headerExtra,
  defaultOpen = true,
  children,
}) => {
  const theme = useTheme();
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const toggleButtonZIndex = 1500;
  const toggleButtonRightOffset = isOpen ? -16 : -36;
  const toggleButtonPalette = theme.palette[toggleButtonColor];

  const handleSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onSearchChange(event.target.value);
    },
    [onSearchChange]
  );

  const handleToggle = useCallback(() => {
    const newIsOpen = !isOpen;
    setIsOpen(newIsOpen);
    onSidebarToggle?.(newIsOpen);
  }, [isOpen, onSidebarToggle]);

  return (
    <Box
      sx={{
        position: "absolute",
        left: leftOffset,
        top: 0,
        height: "100%",
        display: "flex",
        transition: theme.transitions.create("left", {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.enteringScreen,
        }),
      }}
    >
      <Card
        sx={{
          position: "relative",
          zIndex,
          width: isOpen ? width : 0,
          height: "100%",
          transition: theme.transitions.create("width", {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <CardContent
          sx={{
            padding: 1.5,
            paddingBottom: "0 !important",
            display: "flex",
            flexDirection: "column",
            height: "100%",
            gap: 1.5,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
            }}
          >
            <Typography
              variant="subtitle2"
              component="div"
              sx={{
                fontSize: "0.75rem",
                fontWeight: 500,
              }}
            >
              {title} ({totalCount})
            </Typography>
            {headerExtra}
          </Box>

          <TextField
            size="small"
            placeholder={searchPlaceholder}
            value={searchQuery}
            onChange={handleSearchChange}
            disabled={isLoading}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: "1rem" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiInputBase-root": {
                fontSize: "0.75rem",
                height: 32,
                minHeight: 32,
              },
              "& .MuiInputBase-input": {
                padding: "4px 8px",
              },
            }}
          />

          <Box
            sx={{
              flex: 1,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {children}
          </Box>

          <Box
            sx={{
              minHeight: 32,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 0.75,
              py: 1,
              color: "text.secondary",
            }}
          >
            <Typography variant="caption" sx={{ fontSize: "0.7rem" }}>
              Showing {displayedCount} of {totalCount}
            </Typography>
            {isLoadingMore && <CircularProgress size={14} />}
          </Box>
        </CardContent>
      </Card>

      <IconButton
        onClick={handleToggle}
        size="small"
        sx={{
          minWidth: 28,
          width: 36,
          height: 28,
          borderRadius: 999,
          bgcolor: "background.paper",
          border: "1px solid",
          borderColor: alpha(toggleButtonPalette.main, 0.35),
          p: 0,
          position: "absolute",
          right: toggleButtonRightOffset,
          top: toggleButtonTop,
          transform: "translateY(-50%)",
          "&:hover": {
            bgcolor: "background.paper",
            borderColor: alpha(toggleButtonPalette.main, 0.55),
          },
          "& .MuiSvgIcon-root": {
            color: alpha(toggleButtonPalette.dark, 0.8),
          },
          zIndex: toggleButtonZIndex,
        }}
      >
        {isOpen ? (
          <>
            <Box sx={{ mr: -0.25, display: "inline-flex" }}>{icon}</Box>
            <ChevronLeftIcon sx={{ ml: -0.25, fontSize: 15 }} />
          </>
        ) : (
          <>
            <ChevronLeftIcon
              sx={{
                mr: -0.25,
                fontSize: 15,
                transform: "rotate(180deg)",
                transition: theme.transitions.create("transform", {
                  duration: theme.transitions.duration.enteringScreen,
                }),
              }}
            />
            <Box sx={{ ml: -0.25, display: "inline-flex" }}>{icon}</Box>
          </>
        )}
      </IconButton>
    </Box>
  );
};
